// WindFillLayer — Windy.com-style colored speed gradient overlay.
//
// Shares the wind PNG (R = u-component, G = v-component, ±100 m/s clamp)
// with WindParticleLayer.  Draws a tessellated quad whose vertices live in
// world Mercator [0,1]² and are projected through MapLibre's own
// `projectTile()` function so they wrap correctly onto the globe (and clip
// at the horizon) in globe-projection mode, then auto-flatten back to
// Mercator at high zoom.
//
// Color stops (knots):
//   0-5    slate grey/blue
//   5-10   light blue
//   10-20  green
//   20-30  yellow
//   30-40  orange
//   40-50  red
//   50+    purple → deep purple
//
// Rendered with constant alpha (~0.45) so the underlying chart stays visible
// and the particles read clearly on top.

import type {
  CustomLayerInterface,
  Map as MlMap,
  CustomRenderMethodInput,
} from 'maplibre-gl';

// The mesh tessellation. 64×64 quads = ~8k triangles. Higher than strictly
// needed for Mercator-y curvature, but on the globe each vertex bends
// independently so denser = smoother sphere silhouette.
const MESH_N = 64;

// Vertex shader template — `__PRELUDE__` is replaced at link time with the
// projection prelude MapLibre hands us (which defines `projectTile`).
const VS_TEMPLATE = /* glsl */ `
__PRELUDE__
attribute vec2 a_world;             // world Mercator XY in [0,1]^2
attribute vec2 a_uv;                // wind-raster UV
varying vec2 v_uv;
void main() {
  v_uv = a_uv;
  // projectTile expects a Mercator [0,1]² position and outputs clip space,
  // handling globe wrap + horizon clip transparently.
  gl_Position = projectTile(a_world);
}
`;

const FS = /* glsl */ `
precision highp float;
uniform sampler2D u_wind;
uniform vec2 u_wind_min;
uniform vec2 u_wind_max;
uniform float u_alpha;
varying vec2 v_uv;

vec3 gradientKnots(float kt) {
  vec3 c0  = vec3(0.45, 0.52, 0.62);  //   0 kt  slate grey-blue
  vec3 c5  = vec3(0.55, 0.78, 0.93);  //   5 kt  light sky blue
  vec3 c10 = vec3(0.40, 0.85, 0.45);  //  10 kt  fresh green
  vec3 c20 = vec3(0.95, 0.92, 0.25);  //  20 kt  bright yellow
  vec3 c30 = vec3(0.98, 0.60, 0.20);  //  30 kt  orange
  vec3 c40 = vec3(0.93, 0.25, 0.32);  //  40 kt  red
  vec3 c50 = vec3(0.65, 0.20, 0.65);  //  50 kt  magenta-purple
  vec3 c65 = vec3(0.28, 0.10, 0.38);  //  65+ kt deep purple

  vec3 col = c0;
  col = mix(col, c5,  smoothstep( 0.0,  5.0, kt));
  col = mix(col, c10, smoothstep( 5.0, 10.0, kt));
  col = mix(col, c20, smoothstep(10.0, 20.0, kt));
  col = mix(col, c30, smoothstep(20.0, 30.0, kt));
  col = mix(col, c40, smoothstep(30.0, 40.0, kt));
  col = mix(col, c50, smoothstep(40.0, 50.0, kt));
  col = mix(col, c65, smoothstep(50.0, 65.0, kt));
  return col;
}

void main() {
  if (v_uv.x < 0.0 || v_uv.x > 1.0 || v_uv.y < 0.0 || v_uv.y > 1.0) {
    discard;
  }

  // GFS PNG stored S→N; UV y=0 = 90°N → flip y when sampling.
  vec2 texUV = vec2(v_uv.x, 1.0 - v_uv.y);
  vec4 px = texture2D(u_wind, texUV);

  vec2 uv_mps = mix(u_wind_min, u_wind_max, px.rg);
  float speed_mps = length(uv_mps);
  float speed_kt = speed_mps * 1.94384;

  vec3 col = gradientKnots(speed_kt);

  // Soft fade-in at the calm end so windless areas don't paint a hard
  // slate sheet over the chart.
  float intensity = smoothstep(0.5, 3.0, speed_kt) * 0.55 + 0.45;

  float a = u_alpha * intensity * px.a;
  gl_FragColor = vec4(col * a, a);   // pre-multiplied
}
`;

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src); gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) ?? '';
    gl.deleteShader(sh);
    throw new Error(`WindFill shader error: ${log}\n--- source ---\n${src}`);
  }
  return sh;
}
function linkProgram(gl: WebGLRenderingContext, vs: string, fs: string): WebGLProgram {
  const v = compileShader(gl, gl.VERTEX_SHADER, vs);
  const f = compileShader(gl, gl.FRAGMENT_SHADER, fs);
  const p = gl.createProgram()!;
  gl.attachShader(p, v); gl.attachShader(p, f);
  gl.linkProgram(p); gl.deleteShader(v); gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    throw new Error(`WindFill link error: ${gl.getProgramInfoLog(p)}`);
  }
  return p;
}

export interface WindFillLayerOptions {
  alpha?: number;
  windClampMps?: number;
}

export class WindFillLayer implements CustomLayerInterface {
  readonly id = 'wind-fill';
  readonly type = 'custom' as const;
  readonly renderingMode = '2d' as const;

  alpha: number;
  windClampMps: number;

  private gl: WebGLRenderingContext | null = null;
  private map: MlMap | null = null;
  // Per-projection-variant program cache (mercator vs globe vs in-between).
  private progCache: Map<string, WebGLProgram> = new Map();
  private vertBuf: WebGLBuffer | null = null;
  private idxBuf: WebGLBuffer | null = null;
  private idxCount = 0;
  private windTexture: WebGLTexture | null = null;
  private windImg: HTMLImageElement | null = null;
  private inited = false;

  constructor(opts: WindFillLayerOptions = {}) {
    this.alpha = opts.alpha ?? 0.45;
    this.windClampMps = opts.windClampMps ?? 100;
  }

  onAdd(map: MlMap, gl: WebGLRenderingContext) {
    this.map = map; this.gl = gl;

    // Build (N+1)×(N+1) grid covering world Mercator [0,1]² and indices.
    const N = MESH_N;
    const verts = new Float32Array((N + 1) * (N + 1) * 4);
    const indices = new Uint16Array(N * N * 6);
    const PI = Math.PI;
    let v = 0;
    for (let j = 0; j <= N; j++) {
      const wy = j / N;
      const latRad = Math.atan(Math.sinh(PI * (1 - 2 * wy)));
      const latDeg = latRad * 180 / PI;
      const uvY = (90 - latDeg) / 180;
      for (let i = 0; i <= N; i++) {
        const wx = i / N;
        verts[v++] = wx;
        verts[v++] = wy;
        verts[v++] = wx;
        verts[v++] = uvY;
      }
    }
    let k = 0;
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        const a = j * (N + 1) + i;
        const b = a + 1;
        const c = a + (N + 1);
        const d = c + 1;
        indices[k++] = a; indices[k++] = b; indices[k++] = c;
        indices[k++] = b; indices[k++] = d; indices[k++] = c;
      }
    }
    this.idxCount = indices.length;

    this.vertBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertBuf);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);

    this.idxBuf = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.idxBuf);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);

    this.inited = true;
    if (this.windImg) this.setWindImage(this.windImg);
  }

  onRemove() {
    const gl = this.gl; if (!gl) return;
    for (const p of this.progCache.values()) gl.deleteProgram(p);
    this.progCache.clear();
    if (this.vertBuf) gl.deleteBuffer(this.vertBuf);
    if (this.idxBuf) gl.deleteBuffer(this.idxBuf);
    if (this.windTexture) gl.deleteTexture(this.windTexture);
    this.vertBuf = null; this.idxBuf = null; this.windTexture = null;
    this.inited = false;
  }

  setWindImage(img: HTMLImageElement) {
    const gl = this.gl;
    if (!gl || !this.inited) { this.windImg = img; return; }
    if (this.windTexture) gl.deleteTexture(this.windTexture);
    this.windImg = img;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.bindTexture(gl.TEXTURE_2D, null);
    this.windTexture = t;
    this.map?.triggerRepaint();
  }

  setAlpha(a: number) {
    this.alpha = Math.max(0, Math.min(1, a));
    this.map?.triggerRepaint();
  }

  /** Compile (or fetch from cache) a program built with the current projection prelude. */
  private getProgram(gl: WebGLRenderingContext, prelude: string, variantName: string): WebGLProgram {
    let prog = this.progCache.get(variantName);
    if (prog) return prog;
    const vs = VS_TEMPLATE.replace('__PRELUDE__', prelude);
    prog = linkProgram(gl, vs, FS);
    this.progCache.set(variantName, prog);
    return prog;
  }

  render(gl: WebGLRenderingContext, args: CustomRenderMethodInput) {
    if (!this.inited || !this.vertBuf || !this.idxBuf || !this.windTexture) return;
    // Guard against lost WebGL context (same as WindParticleLayer).
    if (gl.isContextLost()) return;

    const pd = args.defaultProjectionData;
    const sd = args.shaderData;
    if (!sd || !sd.vertexShaderPrelude) return;

    let prog: WebGLProgram;
    try {
      prog = this.getProgram(gl, sd.vertexShaderPrelude, sd.variantName);
    } catch (e) {
      console.error('[WindFill] shader compile failed:', e);
      return;
    }

    // Preserve GL state
    const blend = gl.isEnabled(gl.BLEND);
    const depth = gl.isEnabled(gl.DEPTH_TEST);
    const stencil = gl.isEnabled(gl.STENCIL_TEST);

    gl.useProgram(prog);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.STENCIL_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    // Bind the projection uniforms MapLibre's prelude expects.
    gl.uniformMatrix4fv(gl.getUniformLocation(prog, 'u_projection_matrix'), false, pd.mainMatrix as Float32Array);
    gl.uniformMatrix4fv(gl.getUniformLocation(prog, 'u_projection_fallback_matrix'), false, pd.fallbackMatrix as Float32Array);
    gl.uniform4f(gl.getUniformLocation(prog, 'u_projection_tile_mercator_coords'),
      pd.tileMercatorCoords[0], pd.tileMercatorCoords[1], pd.tileMercatorCoords[2], pd.tileMercatorCoords[3]);
    gl.uniform4f(gl.getUniformLocation(prog, 'u_projection_clipping_plane'),
      pd.clippingPlane[0], pd.clippingPlane[1], pd.clippingPlane[2], pd.clippingPlane[3]);
    gl.uniform1f(gl.getUniformLocation(prog, 'u_projection_transition'), pd.projectionTransition);

    // Layer uniforms
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.windTexture);
    gl.uniform1i(gl.getUniformLocation(prog, 'u_wind'), 0);
    gl.uniform2f(gl.getUniformLocation(prog, 'u_wind_min'), -this.windClampMps, -this.windClampMps);
    gl.uniform2f(gl.getUniformLocation(prog, 'u_wind_max'),  this.windClampMps,  this.windClampMps);
    gl.uniform1f(gl.getUniformLocation(prog, 'u_alpha'), this.alpha);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertBuf);
    const locWorld = gl.getAttribLocation(prog, 'a_world');
    const locUv = gl.getAttribLocation(prog, 'a_uv');
    gl.enableVertexAttribArray(locWorld);
    gl.vertexAttribPointer(locWorld, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(locUv);
    gl.vertexAttribPointer(locUv, 2, gl.FLOAT, false, 16, 8);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.idxBuf);
    gl.drawElements(gl.TRIANGLES, this.idxCount, gl.UNSIGNED_SHORT, 0);

    // Restore GL state
    if (blend) gl.enable(gl.BLEND); else gl.disable(gl.BLEND);
    if (depth) gl.enable(gl.DEPTH_TEST); else gl.disable(gl.DEPTH_TEST);
    if (stencil) gl.enable(gl.STENCIL_TEST); else gl.disable(gl.STENCIL_TEST);
  }
}
